
// Libraries
import { PropType, defineComponent } from 'vue';

// Components
import AuthView from 'client/containers/user/Views/AuthView.vue';
import ButtonLoginSwitch from 'client/components/ButtonLoginSwitch.vue';
import DuoRedirectHandler from 'client/containers/user/login/mfa/DuoRedirectHandler.vue';
import JumpcloudGoHandler from 'client/containers/user/SsoStepUp/JumpcloudGoHandler.vue';
import MfaView from 'client/containers/user/Views/MfaView.vue';
import OtpUserLoginHandler from 'client/containers/user/login/mfa/OtpUserLoginHandler.vue';
import PushUserLoginHandler from 'client/containers/user/login/mfa/PushUserLoginHandler.vue';
import TotpUserLoginHandler from 'client/containers/user/login/mfa/TotpUserLoginHandler.vue';
import WebAuthnUserLoginHander from 'client/containers/user/login/mfa/WebAuthnUserLoginHandler.vue';

// Constants and Types
import { LoginConsts } from 'client/util/Constants';
import {
  SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
  SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
  createSmsOtpLoginSession,
} from 'client/util/Constants/smsOtpLoginSession';
import { useLoginLabels } from 'client/util/Constants/LoginLabels';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Utils
import RequestError from 'client/services/response/RequestError';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';
import getLastUsedFactor from 'client/containers/user/MfaComponents/mfaUtils/getLastUsedFactor';
import routerService from 'client/services/routerService';

// Store
import { mapActions } from 'vuex';

import type { MfaFactor } from 'client/types/Auth';
import type {
  SmsOtpLoginSession,
  SmsOtpLoginSessionPatch,
} from 'client/util/Constants/smsOtpLoginSession';

const { responseMessages } = LoginConsts;
const { mfaNames } = MfaConstants;
const {
  loginError,
} = responseMessages;

const componentsUnderControl = [
  {
    id: mfaNames.duo,
    componentName: DuoRedirectHandler.name,
  },
  {
    id: mfaNames.sms_otp,
    componentName: OtpUserLoginHandler.name,
  },
  {
    id: mfaNames.durt,
    componentName: JumpcloudGoHandler.name,
  },
  {
    id: mfaNames.durt_uv,
    componentName: JumpcloudGoHandler.name,
  },
  {
    id: mfaNames.totp,
    componentName: TotpUserLoginHandler.name,
  },
  {
    id: mfaNames.webauthn,
    componentName: WebAuthnUserLoginHander.name,
  },
  {
    id: mfaNames.push,
    componentName: PushUserLoginHandler.name,
  },
];

const getCurrentComponentName = getCurrentComponentNameFromList(componentsUnderControl);

export default defineComponent({
  name: 'SsoStepUpController',

  components: {
    AuthView,
    DuoRedirectHandler,
    ButtonLoginSwitch,
    JumpcloudGoHandler,
    MfaView,
    OtpUserLoginHandler,
    PushUserLoginHandler,
    TotpUserLoginHandler,
    WebAuthnUserLoginHander,
  },

  data() {
    return {
      // using undefined so we display 0
      errorMessageDisplayDuration: undefined as number | undefined,
      /** Set when user has clicked JC GO login (hide "choose a different way" from then on) */
      jcGoChosen: false,
    };
  },

  props: {
    errorMessage: {
      type: String,
      default: '',
    },
    factors: {
      type: Array as PropType<MfaFactor[]>,
      required: true,
    },
    mfaType: {
      type: String,
      default: '',
    },
    smsLoginSession: {
      type: Object as PropType<SmsOtpLoginSession>,
      default: () => createSmsOtpLoginSession(),
    },
  },

  setup() {
    const loginLabels = useLoginLabels();
    const mfaLabels = useMfaLabels();

    return {
      loginLabels,
      mfaLabels,
    };
  },

  computed: {
    componentHandlers() {
      return {
        // Used in: Push
        clearCredentials: this.clearCredentials,
        // Used in: Totp, OTP, Push, Duo, WebAuthN
        error: this.onMfaError,
        // Used in: Go – hide other options after user clicks Login with JC GO
        jcGoStarted: this.onJcGoStarted,
        // Used in: Push, WebAuthN
        resetErrorMessage: this.resetErrorMessage,
        // Used in: Totp, OTP, Go, Push, Duo, WebAuthN
        success: this.onSuccess,
        // Used in: SMS OTP
        'sms-session-patch': this.forwardSmsLoginSessionPatch,
      };
    },

    componentName() {
      return getCurrentComponentName(this.mfaType);
    },

    componentProps() {
      const base = {
        // Push
        controllingQueryParams: {},
        // Push
        hasPendingErrorMessage: this.hasPendingErrorMessage,
        // Go: auto-trigger only when single factor so user can choose TOTP/WebAuthn otherwise
        isOnlyFactor: this.factors.length === 1,
        mfaType: this.mfaType,
      };

      if (this.mfaType === mfaNames.sms_otp) {
        return {
          ...base,
          smsLoginSession: this.smsLoginSession,
        };
      }

      return base;
    },

    hasPendingErrorMessage() {
      const remainingDuration = this.errorMessageDisplayDuration || -1;
      return remainingDuration >= 0;
    },

    headerText() {
      return this.isJcGoMfa ? '' : this.mfaLabels.verifyIdentity;
    },

    isJcGoMfa() {
      const goMfaTypes = [mfaNames.durt, mfaNames.durt_uv];
      return goMfaTypes.includes(this.mfaType);
    },

    /** Hide "choose a different way" only after user has clicked Login with JC GO */
    hideAlternateFactors(): boolean {
      return this.isJcGoMfa && this.jcGoChosen;
    },
  },

  watch: {
    mfaType(newType: string) {
      const goMfaTypes = [mfaNames.durt, mfaNames.durt_uv];
      if (newType && !goMfaTypes.includes(newType)) {
        this.jcGoChosen = false;
      }
    },
  },

  created() {
    this.validateInitialStep();
  },

  methods: {
    ...mapActions('StepUpModel', [
      'setStateId',
    ]),

    clearCredentials() {
      // clearing the state id allows the user to authenticate through push after denial
      (this as any).setStateId('');
    },

    forwardSmsLoginSessionPatch(patch: SmsOtpLoginSessionPatch) {
      this.$emit('sms-session-patch', patch);
    },

    handleErrorCountdown() {
      const errorCountdown = setInterval(() => {
        if ((this.errorMessageDisplayDuration as number) < 1) {
          this.resetErrorMessage();
          clearInterval(errorCountdown);
        }

        (this.errorMessageDisplayDuration as number) -= 1;
      }, 1000);
    },

    onMfaError(error: any) {
      const errorMessage = error instanceof RequestError ? error.message : loginError;
      this.errorMessageDisplayDuration = error instanceof RequestError
        ? error.displayDuration : undefined;

      if (this.errorMessageDisplayDuration && this.errorMessageDisplayDuration > 0) {
        this.handleErrorCountdown();
      }

      // Indicates invalid otp attempts > max attempts
      if (error?.status === 403) {
        this.clearCredentials();
        routerService.goToUserLogin({
          params: { error: errorMessage },
        });
      } else if (
        error instanceof RequestError
        && (error.statusCode === SMS_OTP_IDLE_TIMEOUT_STATUS_CODE
          || error.statusCode === SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE)
      ) {
        this.forwardSmsLoginSessionPatch({ removedFromChooser: true });
        this.setErrorMessage(errorMessage);
      } else {
        this.setErrorMessage(errorMessage);
      }
    },

    resetErrorMessage() {
      this.errorMessageDisplayDuration = undefined;
      this.$emit('resetErrorMessage');
    },

    onSuccess() {
      // no-op: Redirect triggered by axios response interceptor
    },

    setErrorMessage(message: string) {
      this.$emit('setErrorMessage', message);
    },

    onJcGoStarted() {
      this.jcGoChosen = true;
    },

    validateInitialStep() {
      const { mfaType } = getLastUsedFactor(this.factors);
      const lastFactor = this.factors.find(
        (factor) => factor.name === mfaType,
      );

      if (lastFactor) {
        this.$router.replace(lastFactor.route);
      }
    },
  },
});
